import {
  Component,
  inject,
  OnDestroy,
  viewChild,
} from "@angular/core";
import { InstructionAdminComponent } from "./instruction-admin/instruction-admin.component";
import { InstructionDefinitionComponent } from "./instruction-definition/instruction-definition.component";

import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatTabsModule } from "@angular/material/tabs";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ButtonsModule } from "@progress/kendo-angular-buttons";
import { ProfileService } from "../../../../services/profile.service";
import { StepperModule } from "@progress/kendo-angular-layout";
import { UtilityStepperComponent } from "../../../../shared/utility-stepper/utility-stepper.component";
import {
  SVGIcon,
  pencilIcon,
  gearsIcon,
  myspaceIcon,
  xOutlineIcon,
  checkOutlineIcon,
  shapesIcon,
  fileHeaderIcon,
  lockIcon
} from "@progress/kendo-svg-icons";
import { MatIconModule } from "@angular/material/icon";
import { InstructionBuilderService } from "./services/instruction-builder.service";
import { AlertService } from "../../../../shared/alert/alert.service";
import { AlertEnum } from "../../../../shared/alert/alert.enum";
import { SpinnerService } from "../../../../shared/spinner/spinner.service";
import { UtilitiesService } from "../../../../services/utilities.service";
import { RoutePath, NavItemLabelIds } from "../../../../enums/api.enum";
import { ActivatedRoute, Router } from "@angular/router";
import { UiLayoutService } from "../../../../ui-layout/services/ui-layout.service";
import { InstructionTypesComponent } from "./instruction-types/instruction-types.component";
import { AgentService } from "../../../agent/services/agent.service";
import { InstructionsAiConfigComponent } from "./instructions-ai-config/instructions-ai-config.component";
import { InstructionSummaryComponent } from "./instruction-summary/instruction-summary.component";
import { firstValueFrom } from "rxjs";
import { ApiService } from "../../../../services/api.service";
import { InstructionsService } from "../services/instructions.service";
import { PromptLibraryService } from "../../../ai-chat/services/prompt-library.service";
import { InstructionStep } from "../../enums/automation.enum";
import { INSTRUCTION_STEP_SLUG_MAP, INSTRUCTION_STEP_TO_SLUG } from "../../constants/automation-step-slugs.constant";
import { InstructionType } from "../../../../enums/automation.enum";
import { InstructionSecurityComponent } from "./instruction-security/instruction-security.component";
import { resolveStepSlugIndex } from "../../../../utilities/step-slug";

@Component({
  selector: "app-instruction-builder",
  imports: [
    ButtonsModule,
    MatTabsModule,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    StepperModule,
    FormsModule,
    ReactiveFormsModule,
    InstructionDefinitionComponent,
    InstructionsAiConfigComponent,
    InstructionAdminComponent,
    InstructionTypesComponent,
    InstructionSummaryComponent,
    InstructionSecurityComponent,
    UtilityStepperComponent
],
  templateUrl: "./instruction-builder.component.html",
  styleUrl: "./instruction-builder.component.scss",
})
export class InstructionBuilderComponent implements OnDestroy {

  definitionComponent = viewChild(InstructionDefinitionComponent)
  aiConfigComponent = viewChild(InstructionsAiConfigComponent);
  typesComponent = viewChild(InstructionTypesComponent);
  // TODO reimplement Test step when backend supports it
  // testComponents!: QueryList<InstructionTestComponent>;
  securityComponent = viewChild(InstructionSecurityComponent);
  adminComponent = viewChild(InstructionAdminComponent);
  summaryComponent = viewChild(InstructionSummaryComponent);

  // Expose enum to template
  readonly InstructionStep = InstructionStep;

  // Check if user can manage workflows
  canManage = false;

  // Show admin step if user has admin rights
  showInstructionAdminStep = false;

  // Flag to control validation
  validateOnRender = false;

  // SVG Icons
  readonly definitionIcon: SVGIcon = pencilIcon;
  readonly aiConfigIcon: SVGIcon = gearsIcon;
  // readonly testIcon: SVGIcon = listUnorderedIcon;
  readonly typesIcon: SVGIcon = shapesIcon;
  readonly securityIcon: SVGIcon = lockIcon;
  readonly adminIcon: SVGIcon = myspaceIcon;
  readonly summaryIcon: SVGIcon = fileHeaderIcon;
  readonly errorIcon: SVGIcon = xOutlineIcon;
  readonly successIcon: SVGIcon = checkOutlineIcon;

  // Define steps for the stepper with validation
  steps: {
    label: string;
    svgIcon: SVGIcon;
    isValid: () => boolean;
    validate: boolean;
  }[] = [];
  currentStep = 0;
  instructionId: string | null = null;
  title = "Create Instruction";
  isInstructionOwner: boolean = false;
  // Track validation status for each step - true = valid, false = invalid, null = not validated
  validationStatus: (boolean | null)[] = [];
  // List to hold form validation error messages
  // This will be used to display validation errors in the dialog component for when we implement the import/export functionality
  formValidationErrorList: string[] = [];

  // Services
  #instructionBuilderService = inject(InstructionBuilderService);
  #alertService = inject(AlertService);
  #spinnerService = inject(SpinnerService);
  #utilitiesService = inject(UtilitiesService);
  #router = inject(Router);
  #route = inject(ActivatedRoute);
  #uiLayoutService = inject(UiLayoutService);
  #agentService = inject(AgentService);
  #instructionsService = inject(InstructionsService);
  #profileService = inject(ProfileService);
  #apiService = inject(ApiService);
  #promptLibraryService = inject(PromptLibraryService);

  #currentInstruction = this.#instructionBuilderService.instruction();

  constructor() {
    // Initialize steps array before subscribing to route params so that
    // resolveStepSlugIndex can find the correct step on the first emission.
    this.steps = [
      {
        label: InstructionStep.DEFINITION,
        svgIcon: this.definitionIcon,
        // Function to check if the step is valid, only when validation is requested
        isValid: (): boolean =>
          this.validateOnRender ? this.validateCurrentStep() : true,
        validate: true,
      },
      {
        label: InstructionStep.TYPES,
        svgIcon: this.typesIcon,
        isValid: (): boolean =>
          this.validateOnRender ? this.validateCurrentStep() : true,
        validate: true,
      },
      {
        label: InstructionStep.AI_CONFIG,
        svgIcon: this.aiConfigIcon,
        isValid: (): boolean =>
          this.validateOnRender ? this.validateCurrentStep() : true,
        validate: false, // AI Config step doesn't have required fields, so no validation needed
      },
      {
        label: InstructionStep.SECURITY,
        svgIcon: this.securityIcon,
        isValid: (): boolean =>
          this.validateOnRender ? this.validateCurrentStep() : true,
        validate: false, // Security step doesn't have required fields, so no validation needed
      }
      // {
      //   label: "Test",
      //   svgIcon: this.testIcon,
      //   isValid: (): boolean => this.validateOnRender ? this.isStepValid("test") : true,
      //   validate: false
      // },
    ];

    // Initialize validation status array with nulls (not validated yet)
    this.validationStatus = new Array(this.steps.length).fill(null);

    // Update steps array if user can manage workflows
    const userProfile = this.#profileService.userProfile()?.config;
    this.canManage = userProfile?.canManageWorkflows ?? false;
    if (this.canManage && userProfile?.isAdmin) {
      // Update admin step visibility
      this.showInstructionAdminStep = true;

      // Add admin step if not already present
      // Check if admin step already exists
      const adminStepExists = this.steps.some(
        (step) => step.label === InstructionStep.ADMIN
      );
      if (!adminStepExists) {
        this.steps.push({
          label: InstructionStep.ADMIN,
          svgIcon: this.adminIcon,
          isValid: (): boolean =>
            this.validateOnRender ? this.validateCurrentStep() : true,
          validate: false, // Admin step doesn't have required fields, so no validation needed
        });

        // Add entry to validation status array
        this.validationStatus.push(null);
      }
    }

    // Add summary step at the end
    const summaryStepExists = this.steps.some(
      (step) => step.label === InstructionStep.SUMMARY
    );

    if (!summaryStepExists) {
      this.steps.push({
        label: InstructionStep.SUMMARY,
        svgIcon: this.summaryIcon,
        isValid: (): boolean =>
          this.validateOnRender ? this.validateCurrentStep() : true,
        validate: false, // No validation needed for summary step
      });

      // Add entry to validation status array
      this.validationStatus.push(null);
    }

    // Subscribe to route params AFTER steps are fully initialized so that
    // resolveStepSlugIndex can find the correct step index on the first emission.
    this.#route.params.subscribe((params) => {
        this.#instructionsService.isEditInstructions.set(false);

        if (!params["instructionId"]) {
          // Creating a new instruction — reset to defaults so any previously
          // loaded instruction (edit/clone) doesn't bleed into the form.
          this.#instructionBuilderService.resetInstruction();
          this.#currentInstruction = this.#instructionBuilderService.instruction();
        }

        if (params["instructionId"]) {
          const instructionId = params["instructionId"];
          this.instructionId = instructionId;

          // Only fetch from the API if we haven't already loaded this instruction.
          if (this.#instructionBuilderService.instruction().id === instructionId) {
            // Data already loaded — set flags and resolve the step without re-fetching.
            if (this.#router.url.includes(RoutePath.EditInstruction)) {
              this.title = "Edit Instruction";
              this.#instructionsService.isEditInstructions.set(true);
              const index = resolveStepSlugIndex(params['step'], INSTRUCTION_STEP_SLUG_MAP, this.steps);
              if (index !== -1) this.currentStep = index;
            }
            return;
          }

          this.#spinnerService.show();
          this.#apiService.getInstructionById(instructionId).subscribe({
            next: (instruction) => {
              this.#instructionBuilderService.instruction.set(instruction);
              this.#currentInstruction = instruction;
              // TODO implement when we add owners to the instruction
              // this.isInstructionOwner = this.#instructionBuilderService.instruction().details.owners.some(owner => owner.userId === this.#profileService.userProfile()!.profile.email || this.#profileService.userProfile()!.config?.canManageWorkflows);

              if (this.#router.url.includes(RoutePath.CloneInstruction)) {
                // Clone the instruction
                this.#cloneInstruction();
                this.#instructionsService.isEditInstructions.set(false);
              }

              if (this.#router.url.includes(RoutePath.EditInstruction)) {
                // Update the instruction with the values from the selected instruction
                this.title = "Edit Instruction";
                this.#instructionsService.isEditInstructions.set(true);
              }
            },
            error: (err) => {
              this.#spinnerService.hide();
              this.#alertService.showAlert({
                message: `Failed to retrieve Instruction. Please try again.`,
                type: AlertEnum.error,
              });
              // Reset the instruction to the default values and set the currentInstruction to the default values
              this.#instructionBuilderService.resetInstruction();
              this.#currentInstruction =
                this.#instructionBuilderService.instruction();
              this.#instructionsService.isEditInstructions.set(false);
              console.error(err);
            },
            complete: () => {
              this.#spinnerService.hide();
            },
          });
        }

        // Resolve the initial step from the optional :step URL segment.
        // Only applicable for edit routes (edit-instruction/:id/:step).
        if (params['instructionId'] && this.#router.url.includes(RoutePath.EditInstruction)) {
          const index = resolveStepSlugIndex(params['step'], INSTRUCTION_STEP_SLUG_MAP, this.steps);
          if (index !== -1) this.currentStep = index;
        }
    });
  }

  ngOnDestroy(): void {
    // Only reset if navigating away from the instruction builder entirely.
    // When stepping between steps the component is destroyed/recreated but
    // the target URL still contains the builder path — in that case we must
    // preserve the service signal so the guard can skip the API call.
    const targetUrl = this.#router.getCurrentNavigation()?.finalUrl?.toString() ?? '';
    const stayingInBuilder =
      targetUrl.includes(RoutePath.EditInstruction) ||
      targetUrl.includes(RoutePath.CloneInstruction) ||
      targetUrl.includes(RoutePath.CreateInstruction);

    if (!stayingInBuilder) {
      this.reset(false);
    }
    this.#instructionsService.isEditInstructions.set(false);
  }

  /**
   * Reset the instruction form to default values
   * @param showAlert Whether to show an alert after resetting
   */
  reset(showAlert = true): void {
    this.#instructionBuilderService.resetInstruction();
    this.#currentInstruction = this.#instructionBuilderService.instruction();

    // Reset validation status
    this.validationStatus = new Array(this.steps.length).fill(null);

    // Reset step icons to their original icons
    this.steps.forEach((step, index) => {
      step.svgIcon = this.getOriginalIcon(step.label);
    });

    // Get all components that contain forms
    const allComponents = [
      ...(this.definitionComponent() ? [this.definitionComponent()!] : []),
      ...(this.aiConfigComponent() ? [this.aiConfigComponent()!] : []),
      ...(this.typesComponent() ? [this.typesComponent()!] : []),
      // ...this.testComponents.toArray(), // TODO reimplement Test step when backend supports it
      ...(this.adminComponent() ? [this.adminComponent()!] : []),
    ];


    // Reset all form controls
    allComponents.forEach((component) => {
      const form = component.instructionForm;

      Object.keys(form.controls).forEach((key) => {
        if (key in this.#currentInstruction) {
          if (key === "sensitivityLabels") {
            form.controls[key].setValue(
              this.#currentInstruction.details[key]?.map(
                (label: any) => label.id
              ) || []
            );
          } else if (key === "type") {
            // Set the type to 'aiPrompt' regardless of current value
            form.controls[key].setValue(InstructionType.AiPrompt);
          } else {
            form.controls[key].setValue((this.#currentInstruction as any)[key]);
          }
        } else if (key in this.#currentInstruction) {
          form.controls[key].setValue((this.#currentInstruction as any)[key]);
        }
      });

      if (form) {
        Object.keys(form.controls).forEach((key) => {
          form.controls[key].reset();
        });
      }
    });

    if (showAlert) {
      this.#alertService.showAlert({
        message: "Instruction has been reset to default values.",
        type: AlertEnum.success,
      });
    }
  }

  validateAllChildForms(stepName?: string): boolean {
    // Define the steps to validate, each with its component, validation check, and alert message
    const steps = [
      {
        step: InstructionStep.DEFINITION,
        component: this.definitionComponent,
        serviceCheck: () => {
          // Check if definition details (name, description) are present and valid
          const details = this.#instructionBuilderService.instruction().details;
          return details?.name ? true : false;
        },
        alertMsg: 'Please correct the errors in the Definition step before proceeding.'
      },
      {
        step: InstructionStep.TYPES,
        component: this.typesComponent,
        serviceCheck: () => {
          // Check if at least one instruction type is selected and has a prompt
          const details = this.#instructionBuilderService.instruction().details;
          const hasType = details?.type ? true : false;
          const hasPrompt = details?.prompt ? true : false;

          if (!hasPrompt) {
            return false;
          }

          if (hasType && hasPrompt) {
            // Check for type-specific required properties
            switch (details.type) {
              case InstructionType.AiPrompt:
                return !!details.prompt;
              case InstructionType.Agent:
                if (details.agent === null || details.agent === "") {
                  return false;
                } else if (details.sensitivityLabels === null || (details.sensitivityLabels && details.sensitivityLabels.length === 0)) {
                  return false;
                } else {
                  return true;
                }
              case InstructionType.FileUpload:
                return !!details.fileUploadLabel;
              case InstructionType.CollectInformation:
                return !!details.collectInformationLabel;
              default:
                return false;
            }
          } else {
            return false;
          }
        },
        alertMsg: 'Please correct the errors in the Types step before proceeding.'
      }
      // AI Config and Admin steps are always valid as they don't have required fields
    ];

    // Iterate through each step and validate
    for (let i = 0; i < steps.length; i++) {
      const { step, component, serviceCheck, alertMsg } = steps[i];
      // Check if the current step matches the provided stepName or if no stepName is provided
      // This allows validation of all steps if stepName is not specified, else a single step.
      if (stepName === step || !stepName) {
        // If the form is invalid and the service check fails, show alert and set current step
        if (!component()?.isValid?.()) {
          if (!serviceCheck()) {
            this.currentStep = i;
            this.steps[i].svgIcon = this.errorIcon; // Set error icon for the step
            // Mark all controls as touched to show validation errors
            if (component() && component()!.instructionForm) {
              component()?.markFormAsTouched(); // Mark all controls as touched to show validation errors
            }
            this.#alertService.showAlert({ message: alertMsg, type: AlertEnum.error });
            return false;
          }
        } else {
            this.steps[i].svgIcon = this.successIcon; // Set success icon if valid
        }
      }
    }
    // All steps are valid
    return true;
  }

  /**
   * Handles the activate event from Kendo stepper
   * This method is called before the step is changed and can prevent navigation
   * @param event The event containing the requested step index
   */
  onStepActivate(event: any) {
    // Enable validation only when needed for navigation
    this.validateOnRender = true;

    // Validate the current step
    const isCurrentStepValid = this.validateCurrentStep();

    if (!isCurrentStepValid) {
      // Show validation errors
      this.#alertService.showAlert({
        message: `Please correct the validation errors before proceeding.`,
        type: AlertEnum.error,
      });

      // Prevent navigation by returning false
      event.preventDefault();
    }
  }

  /**
   * Handles step change in the stepper
   * @param index Numeric index of the selected step (0: Definition, 1: Types, 2: Admin)
   */
  onStepChange(index: number): void {
    // Update instruction with current form values so they are saved before changing step
    this.updateInstruction();

    // Reset icon for the previous step if it was validated and needs validation
    if (
      this.steps[this.currentStep].validate &&
      this.validationStatus[this.currentStep] !== null
    ) {
      // If the step was validated, update its icon based on validation result
      const isValid = this.validationStatus[this.currentStep];
      this.steps[this.currentStep].svgIcon = isValid
        ? this.successIcon
        : this.errorIcon;
    }

    // The step change will only happen if onStepActivate returned true
    // So we can safely update the current step
    this.currentStep = index;

    // Sync the URL step slug so the active step is bookmarkable / shareable.
    this.#syncStepToUrl(index);
  }

  /**
   * Updates the URL to reflect the currently active step slug, enabling bookmarking and sharing.
   * Replaces the `:step` segment without adding a new browser history entry.
   */
  #syncStepToUrl(index: number): void {
    // Step-slug URLs are only supported for edit routes (edit-instruction/:id/:step).
    if (!this.instructionId || !this.#router.url.includes(RoutePath.EditInstruction)) {
      return;
    }

    const stepLabel = this.steps[index]?.label as InstructionStep | undefined;
    const slug = stepLabel ? INSTRUCTION_STEP_TO_SLUG[stepLabel] : undefined;
    if (!slug) {
      return;
    }

    const urlSegments = this.#router.url.split('/');
    // urlSegments: ['', 'edit-instruction', ':id', ':step?']
    // Replace or append the step slug at index 3.
    urlSegments[3] = slug;

    this.#router.navigateByUrl(urlSegments.join('/'), { replaceUrl: true });
  }

  /**
   * Validates the current step before moving to the next
   * @returns boolean indicating if the current step is valid
   */
  private validateCurrentStep(): boolean {
    let isValid = false;

    // Use numeric indices for the steps to avoid enum comparison issues
    switch (this.currentStep) {
      case 0: // Definition step
        isValid = this.validateAllChildForms(InstructionStep.DEFINITION);
        break;
      case 2: // Types step
        isValid = this.validateAllChildForms(InstructionStep.TYPES);
        break;
      default:
        isValid = true;
        break;
    }

    return isValid;
  }

  onCancel() {
    this.#utilitiesService.goToLastLocation();
  }

  onReset() {
    this.reset();
    this.currentStep = 0; // Reset to the first step
  }

  onSave() {
    this.saveInstruction();
  }

  // TODO: Implement export and import functionality
  // onExport() {
  //   console.log("Export action triggered");
  // }

  // onImport() {
  //   console.log("Import action triggered");
  // }

  /**
   * Save the instruction by updating it with current form values
   * and showing a success alert
   */
  async saveInstruction() {
    // Update instruction with current form values before validation
    await this.updateInstruction();

    // Validate all steps to update their icons
    const invalidStep = this.validateAllChildForms();

    if (!invalidStep) {
      this.#alertService.showAlert({
        message: `Please correct the errors before saving.`,
        type: AlertEnum.error,
      });
      return;
    }

    try {
      // if we are creating a new instruction, use the saveInstruction API call,
      // otherwise use the updateInstruction API call
      if (!this.#instructionsService.isEditInstructions()) {
        const response = await firstValueFrom(
          this.#apiService.saveInstruction(
            this.#instructionBuilderService.instruction().details
          )
        );
      } else {
        const response = await firstValueFrom(
          this.#apiService.updateInstruction(
            this.#instructionBuilderService.instruction().details,
            this.#instructionBuilderService.instruction().id
          )
        );
      }

      // Reload instructions in the Automation Library
      this.#promptLibraryService.loadInstructions();

      this.#alertService.showAlert({
        message: `Your instruction was saved successfully.`,
        type: AlertEnum.success,
      });

      const instructionsResponse = await firstValueFrom(
        this.#apiService.getInstructions()
      );
      this.#instructionsService.instructions.set(instructionsResponse);

      // If there is no meaningful last route, redirect to My Instructions.
      // "Not meaningful" means: empty, the app-load default (/start-conversation),
      // or the user opened a builder URL directly (edit/clone/create-instruction).
      const noMeaningfulOrigin = this.#utilitiesService.isNoMeaningfulOrigin(
        RoutePath.EditInstruction,
        RoutePath.CloneInstruction,
        RoutePath.CreateInstruction
      );

      if (noMeaningfulOrigin) {
        this.#uiLayoutService.setSelectedNavItem(NavItemLabelIds.MyInstructions);
        this.#utilitiesService.locationFrom = `/${RoutePath.MyInstructions}`;
      }

      const selectedNavItem = this.#uiLayoutService.getSelectedNavItem(this.#utilitiesService.locationFrom.slice(1));

      this.#uiLayoutService.setSelectedNavItem(selectedNavItem?.id!);

      this.#router.navigateByUrl(this.#utilitiesService.locationFrom); // Redirect to last location
    } catch (error) {
      this.#alertService.showAlert({
        message: `Failed to save "${
          this.#instructionBuilderService.instruction().details.name
        }".`,
        type: AlertEnum.error,
      });
      console.error("saveInstruction", error);
    }
  }

  /**
   * Update the instruction with the current form values
   * @param json Optional JSON data to import instead of form values
   */
  async updateInstruction(json?: any) {
    // Get all components that contain forms
    const allComponents = [
      ...(this.definitionComponent() ? [this.definitionComponent()!] : []),
      ...(this.aiConfigComponent() ? [this.aiConfigComponent()!] : []),
      ...(this.typesComponent() ? [this.typesComponent()!] : []),
      ...(this.securityComponent() ? [this.securityComponent()!] : []),
      // ...this.testComponents.toArray(), // TODO reimplement Test step when backend supports it
      ...(this.adminComponent() ? [this.adminComponent()!] : []),
    ];

    // Create a new instruction object for immutability
    let updatedInstruction = {
      ...this.#instructionBuilderService.instruction(),
    };

    allComponents.forEach((component) => {
      const form = component.instructionForm;

      // If we're importing from JSON, use that data instead of form values
      if (json) {
        // TODO when we implement import functionality.
        updatedInstruction = json;
      } else {
        // Update the instruction values with form values from each component
        allComponents.forEach((component) => {
          if (form) {
            // Update the instruction values with the form values
            Object.keys(form.controls).forEach((key) => {
              let value = form.controls[key].value;

              // Explicitly set properties based on key to avoid TypeScript errors
              switch (key) {
                case "id":
                  updatedInstruction.id = value;
                  break;
                case "name":
                  updatedInstruction.details.name = value;
                  break;
                case "description":
                  updatedInstruction.details.description = value;
                  break;
                case "keywords":
                  updatedInstruction.details.keywords = value;
                  break;
                case "model":
                  updatedInstruction.details.model = value;
                  break;
                case "temperature":
                  updatedInstruction.details.temperature = value;
                  break;
                case "topP":
                  updatedInstruction.details.topP = value;
                  break;
                case "type":
                  updatedInstruction.details.type = value;
                  // Set the type now, but we'll handle the prependedPrompt separately
                  // after all form values are processed
                  break;
                // commenting this out for now since in v1, we won't need it.
                // v2 may need it.
                // case "instructionPrompt":
                //   updatedInstruction.instructionPrompt = value;
                //   break;
                case "prompt":
                  updatedInstruction.details.prompt = value;
                  break;
                case InstructionType.Agent:
                  updatedInstruction.details.agent = value;
                  break;
                case "fileUploadLabel":
                  updatedInstruction.details.fileUploadLabel = value;
                  break;
                case "collectInformationLabel":
                  updatedInstruction.details.collectInformationLabel = value;
                  break;
                case "sensitivityLabels":
                  if (value) {
                    if (value.length > 0 && !value[0].label) {
                      value = this.#utilitiesService.findLabelsByIds(value);
                    }
                  }
                  updatedInstruction.details.sensitivityLabels = value;
                  break;
                case "isEnabled":
                  updatedInstruction.details.isEnabled = value;
                  break;
                case "isUniversal":
                  updatedInstruction.details.isUniversal = value;
                  break;
                case "isAdvertised":
                  updatedInstruction.details.isAdvertised = value;
                  break;
                case "isAutoApproved":
                  updatedInstruction.details.isAutoApproved = value;
                  break;
                case "notifyAutoApproval":
                  updatedInstruction.details.notifyAutoApproval = value;
                  break;
                case "owners":
                  updatedInstruction.details.owners = value;
                  break;
                case "users":
                  updatedInstruction.details.users = value;
                  break;
                case "groups":
                  updatedInstruction.details.groups = value;
                  break;
                default:
                  // For any additional properties not in the interface
                  // Use type assertion to allow dynamic property assignment
                  (updatedInstruction as any)[key] = value;
              }

              if (this.#checkKeyExists(key)) {
                (updatedInstruction as any)[key] = value;
              }
            });
          }
        });
      }
    });

    // Handle the prependedPrompt based on instruction type after all form values are processed
    if (updatedInstruction.details.type === InstructionType.Agent) {
      // For agent type, get the agent name from the service
      const agentId = updatedInstruction.details.agent;
      if (agentId) {
        // Fetch the agent name using the agent ID
        const agentName = await this.#agentService.getAgentNameFromId(agentId);
        if (agentName) {
          updatedInstruction.details.prependedPrompt = `Use the ${agentName} agent. `;
        } else {
          // Fallback if agent name can't be retrieved
          updatedInstruction.details.prependedPrompt = `Use the selected agent. `;
        }
      }
    } else if (updatedInstruction.details.type === InstructionType.FileUpload) {
      updatedInstruction.details.prependedPrompt =
        "Use the Read CGI Federal File agent to review the file ";
    } else if (updatedInstruction.details.type === InstructionType.CollectInformation) {
      updatedInstruction.details.prependedPrompt = "";
    } else {
      // Default case for AiPrompt or any other type
      updatedInstruction.details.prependedPrompt = "";
    }

    // Update the instruction in the service
    this.#instructionBuilderService.instruction.set(updatedInstruction);

    if (json) {
      this.#alertService.showAlert({
        message:
          "Instruction data has been successfully imported. Please save the Instruction to apply changes.",
        type: AlertEnum.success,
      });
    }
  }

  /**
   * Clone the current instruction
   */
  async #cloneInstruction(shouldSaveAndRedirect: boolean = false) {
    // update the name so that it is (Clone) <original name>
    this.title = "Clone Instruction";
    this.#instructionBuilderService.instruction.set({
      ...this.#instructionBuilderService.instruction(),
      id: "", // Set to empty string to generate a new ID
      details: {
        ...this.#instructionBuilderService.instruction().details,
        name: `(Clone) ${
          this.#instructionBuilderService.instruction().details.name
        }`,
      },
    });

    this.#currentInstruction = this.#instructionBuilderService.instruction();

    if (shouldSaveAndRedirect) {
      try {
        const response = await firstValueFrom(
          this.#apiService.saveInstruction(
            this.#instructionBuilderService.instruction().details
          )
        );

        // If there is no meaningful last route, redirect to My Instructions.
        const noMeaningfulOriginClone = this.#utilitiesService.isNoMeaningfulOrigin(
          RoutePath.EditInstruction,
          RoutePath.CloneInstruction
        );

        if (noMeaningfulOriginClone) {
          this.#utilitiesService.locationFrom = `/${RoutePath.MyInstructions}`;
        }

        this.#router.navigateByUrl(this.#utilitiesService.locationFrom); // Redirect to last location
      } catch (error) {
        this.#alertService.showAlert({
          message: `Failed to clone "${
            this.#instructionBuilderService.instruction().details.name
          }".`,
          type: AlertEnum.error,
        });
        console.error("cloneInstruction", error);
      }
    }
  }

  /**
   * Check if the key exists in the instruction Interface
   * @param keyName The key to check
   */
  #checkKeyExists(keyName: string): boolean {
    const instruction =
      this.#instructionBuilderService.createInstructionDefaultValue();
    return keyName in instruction;
  }

  /**
   * Get error message based on error key and error value
   * INFO leaving this in here for when we do the import/export functionality
   */
  #getErrorMessage(errorKey: string, errorValue: any): string {
    const errorMessages: { [key: string]: string } = {
      required: "This field is required.",
      name: errorValue,
      sensitivityLabelInvalid:
        "At least one sensitivity label must be selected.",
      // Add other error messages as needed
    };
    return errorMessages[errorKey] || "Invalid field.";
  }

  /**
   * Handles validation status changes from the stepper component
   * @param status The updated validation status array
   */
  onValidationStatusChange(status: (boolean | null)[]): void {
    this.validationStatus = status;

    // Apply appropriate icons based on validation status
    this.steps.forEach((step, index) => {
      if (index < status.length) {
        if (status[index] === true) {
          // Valid - set success icon
          step.svgIcon = this.successIcon;
        } else if (status[index] === false) {
          // Invalid - set error icon
          step.svgIcon = this.errorIcon;
        } else {
          // Not validated or null - reset to original icon
          step.svgIcon = this.getOriginalIcon(step.label);
        }
      }
    });
  }

  /**
   * Gets the original icon for a given step label
   * @param label The step label to get the icon for
   * @returns The original SVG icon for the step
   */
  getOriginalIcon(label: string): SVGIcon {
    switch (label) {
      case InstructionStep.DEFINITION:
        return this.definitionIcon;
      case InstructionStep.AI_CONFIG:
        return this.aiConfigIcon;
      case InstructionStep.TYPES:
        return this.typesIcon;
      case InstructionStep.ADMIN:
        return this.adminIcon;
      default:
        return this.summaryIcon;
    }
  }
}
